import { type components, type operations } from "@njbsoft/types/customer";
import {
  DEVICE_SORTS,
  DEVICE_STATUS_FILTERS,
  type DeviceSort,
  type DeviceStatusFilter,
} from "./device-list";
import { type PropertySummaryBranch } from "./home-standing";
import { enumParam, pageParam, textParam, uuidParam } from "./search-params";

type DeviceLifecycleStatus = components["schemas"]["DeviceLifecycleStatus"];
type BilledQuery = NonNullable<
  operations["listCustomerBilledInstallations"]["parameters"]["query"]
>;

export type PropertiesTab = "properties" | "devices" | "billed";
export type BilledSort = NonNullable<BilledQuery["sort_by"]>;
export type SortDirection = NonNullable<BilledQuery["sort_dir"]>;

/** The three tabs, in the order the row lists them. */
export const PROPERTIES_TABS: readonly PropertiesTab[] = ["properties", "devices", "billed"];

/** The Properties tab's `status` values, in the order its menu lists them. */
export const PROPERTY_STATUS_FILTERS: readonly PropertySummaryBranch[] = [
  "needs_test",
  "under_review",
  "on_schedule",
  "no_devices",
];

// Keyed by the generated union, so a lifecycle status the spec adds fails the
// typecheck here until the Billed devices menu lists it too.
const LIFECYCLE_STATUSES: Record<DeviceLifecycleStatus, null> = {
  in_service: null,
  out_of_service: null,
  replaced: null,
  removed: null,
  abandoned: null,
};

/** The Billed devices tab's `status` values, in the order its menu lists them. */
// Object.keys widens to string[]; the keys are exactly the record's union.
export const BILLED_STATUS_FILTERS = Object.keys(LIFECYCLE_STATUSES) as DeviceLifecycleStatus[];

/** The Billed devices tab's sorts, each an API `sort_by`, in the order its menu lists them. */
export const BILLED_SORTS: readonly BilledSort[] = [
  "serial_number",
  "meter_number",
  "facility_name",
  "device_status",
];

/** The Billed devices tab's orders, each an API `sort_dir`, in the order its menu lists them. */
export const SORT_DIRECTIONS: readonly SortDirection[] = ["asc", "desc"];

/** The Properties tab, the default: written as no `tab` at all. */
export interface PropertiesListSearch {
  tab?: undefined;
  q?: string;
  status?: PropertySummaryBranch;
}

export interface DevicesListSearch {
  tab: "devices";
  q?: string;
  /** A facility id. */
  property?: string;
  status?: DeviceStatusFilter;
  /** Absent reads as `urgency`. */
  sort?: DeviceSort;
}

export interface BilledListSearch {
  tab: "billed";
  q?: string;
  status?: DeviceLifecycleStatus;
  /** Absent reads as `meter_number`. */
  sort?: BilledSort;
  /** Absent reads as `asc`. */
  dir?: SortDirection;
  /** A page past the first; the first page is the param's absence. */
  page?: number;
}

/** `/properties`' search: the visible tab and that tab's own filters, sort and page. */
export type PropertiesSearch = PropertiesListSearch | DevicesListSearch | BilledListSearch;

/** Navigates `/properties` to a search; `replace` swaps the history entry instead of adding one. */
export type PropertiesSearchWriter = (next: PropertiesSearch, options?: { replace: true }) => void;

// The params a tab does not hold are left out rather than carried as
// undefined, so the search reads as the URL does.
function withoutUndefined<T extends object>(search: T): T {
  // Dropping the undefined entries leaves every remaining key as `T` types it.
  return Object.fromEntries(Object.entries(search).filter(([, value]) => value !== undefined)) as T;
}

/**
 * The tab, absent or unknown reading as Properties, then only the params that
 * tab holds, each through its validator. Another tab's params, and any value a
 * param cannot hold, are dropped.
 */
export function validatePropertiesSearch(search: Record<string, unknown>): PropertiesSearch {
  const q = textParam(search.q);
  switch (enumParam(search.tab, PROPERTIES_TABS)) {
    case "devices":
      return withoutUndefined<DevicesListSearch>({
        tab: "devices",
        q,
        property: uuidParam(search.property),
        status: enumParam(search.status, DEVICE_STATUS_FILTERS),
        sort: enumParam(search.sort, DEVICE_SORTS),
      });
    case "billed":
      return withoutUndefined<BilledListSearch>({
        tab: "billed",
        q,
        status: enumParam(search.status, BILLED_STATUS_FILTERS),
        sort: enumParam(search.sort, BILLED_SORTS),
        dir: enumParam(search.dir, SORT_DIRECTIONS),
        page: pageParam(search.page),
      });
    default:
      return withoutUndefined<PropertiesListSearch>({
        q,
        status: enumParam(search.status, PROPERTY_STATUS_FILTERS),
      });
  }
}

/** A tab with none of its filters: what a tab switch navigates to. */
export function propertiesTabSearch(tab: PropertiesTab): PropertiesSearch {
  return tab === "properties" ? {} : { tab };
}
